import { useState, useEffect } from 'react';
import { Building2, Package, Activity, Users, AlertCircle, PieChart, BarChart3 } from 'lucide-react';
import { StatCard } from '../components/StatCard';
import { Sidebar } from '../components/Sidebar';
import { MainLayout } from '../components/MainLayout';
import { contagens } from '../data/mockData';
import { formatRelativeTime } from '../lib/utils';
import { atividadeDiaria, produtosPorCategoria } from '../data/graficosData';
import { BarChartAtividadeDiaria } from '../components/charts/BarChartAtividadeDiaria';
import { PieChartProdutosCategoria } from '../components/charts/PieChartProdutosCategoria';
import dashboardService, { DashboardStats } from '../services/dashboardService';

export const Dashboard = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Item 20 (Sprint 3): Collapse All em dados consolidados
  const [showStats, setShowStats] = useState(true);
  const [showCharts, setShowCharts] = useState(true);
  const [showRecentJobs, setShowRecentJobs] = useState(true);

  const collapseAll = () => {
    setShowStats(false);
    setShowCharts(false);
    setShowRecentJobs(false);
  };

  const expandAll = () => {
    setShowStats(true);
    setShowCharts(true);
    setShowRecentJobs(true);
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await dashboardService.getDashboardStats();
        setStats(data);
        setError('');
      } catch (err: any) {
        console.error('Erro ao carregar estatísticas:', err);
        setError(err.message || 'Erro ao carregar dados do dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <>
        <Sidebar />
        <MainLayout className="flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Carregando dashboard...</p>
          </div>
        </MainLayout>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Sidebar />
        <MainLayout className="flex items-center justify-center">
          <div className="text-center text-red-600">
            <AlertCircle className="w-12 h-12 mx-auto mb-4" />
            <p>{error}</p>
          </div>
        </MainLayout>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <MainLayout>
      <div className="p-6">
        {/* Item 20: Collapse/Expand All Button */}
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => {
              const allCollapsed = !showStats && !showCharts && !showRecentJobs;
              if (allCollapsed) {
                expandAll();
              } else {
                collapseAll();
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {(!showStats && !showCharts && !showRecentJobs) ? (
              <>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">▶ Expandir Todos</span>
              </>
            ) : (
              <>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">▼ Recolher Todos</span>
              </>
            )}
          </button>
        </div>

        {/* Banner de Identidade */}
        <div className="mb-8 bg-gradient-to-r from-blue-900 to-blue-700 rounded-xl p-8 text-white shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-800/20 rounded-full blur-3xl"></div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-lg">
                <Package className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-3xl font-bold">AuditLog - Gestão de Inventário</h2>
                <p className="text-blue-100 text-sm mt-1">Controle inteligente de produtos médicos em hospitais</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <p className="text-blue-100 text-xs mb-1">Valor Total Consignado</p>
                <p className="text-xl font-bold">R$ 48.6M</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <p className="text-blue-100 text-xs mb-1">Acuracidade Média</p>
                <p className="text-xl font-bold">96.7%</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <p className="text-blue-100 text-xs mb-1">Próxima Contagem</p>
                <p className="text-xl font-bold">Hoje 14:00</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <p className="text-blue-100 text-xs mb-1">Hospitais Ativos</p>
                <p className="text-xl font-bold">12</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid - 5 cards */}
        {showStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
          <StatCard
            icon={Building2}
            label="Total de Hospitais"
            value={stats?.kpis.totalHospitals.toString() || '0'}
            change="+2 este mês"
            changeType="positive"
            variant="blue"
          />
          <StatCard
            icon={Package}
            label="Total de Produtos"
            value={stats?.kpis.totalProducts.toString() || '0'}
            change="+234 este mês"
            changeType="positive"
            variant="purple"
          />
          <StatCard
            icon={Activity}
            label="Jobs Ativos"
            value={stats?.kpis.activeJobs.toString() || '0'}
            change="em andamento"
            changeType="positive"
            variant="orange"
          />
          <StatCard
            icon={Users}
            label="Jobs Completos"
            value={stats?.kpis.completedJobs.toString() || '0'}
            change="finalizados"
            changeType="positive"
            variant="green"
          />
          <StatCard
            icon={AlertCircle}
            label="Pendências"
            value={stats?.jobsByStatus.find(j => j.status === 'PENDING')?.count.toString() || '0'}
            change="aguardando"
            changeType="positive"
            variant="red"
          />
        </div>
        )}

        {/* Charts Grid */}
        {showCharts && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Gráfico 1 - Contagens por Dia */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Contagens Últimos 7 Dias</h3>
            <BarChartAtividadeDiaria data={atividadeDiaria} height={300} showLegend={false} />
          </div>

          {/* Gráfico 2 - Produtos por Categoria */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Produtos por Categoria</h3>
            <PieChartProdutosCategoria data={produtosPorCategoria} height={300} showLegend={true} />
          </div>
        </div>
        )}

        {/* Tabela de Contagens Recentes */}
        {showRecentJobs && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Contagens Recentes</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Hospital</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Local</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Usuário</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Data/Hora</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Itens</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {stats?.recentJobs && stats.recentJobs.length > 0 ? (
                  stats.recentJobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">Job #{job.id}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{job.hospital}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">-</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{job.created_at}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-white">-</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                          job.status === 'COMPLETED' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' :
                          job.status === 'PENDING' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400' :
                          job.status === 'IN_PROGRESS' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400' :
                          'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400'
                        }`}>
                          {job.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      Nenhum job recente encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>
      </MainLayout>
    </>
  );
};
